import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Building, Coordinates, Prediction } from "../models/data";

export type BuildingState = {
  buildings: Building[];
  nearbyBuildings: Building[];
  selectedBuilding: Building | null;
  userLocation: Coordinates | null;
  userPlusCode: string | null;
  searchResults: Building[];
  searchPredictions: Prediction[];
  sessionToken: string | null;
  followedBuildings: Building[];
};

const initialState: BuildingState = {
  buildings: [],
  nearbyBuildings: [],
  selectedBuilding: null,
  userLocation: null,
  userPlusCode: null,
  searchResults: [],
  searchPredictions: [],
  sessionToken: null,
  followedBuildings: [],
};

const buildingsSlice = createSlice({
  name: "buildings",
  initialState,
  reducers: {
    loadBuildings(state, action: PayloadAction<Building[]>) {
      state.buildings = action.payload;
    },
    setNearbyBuildings(state, action: PayloadAction<Building[]>) {
      const seen = new Set<string>();
      state.nearbyBuildings = action.payload.filter(b => seen.has(b.id) ? false : seen.add(b.id) && true);
    },
    setUserLocation(state, action: PayloadAction<Coordinates | null>) {
      state.userLocation = action.payload;
    },
    setUserPlusCode(state, action: PayloadAction<string | null>) {
      state.userPlusCode = action.payload;
    },
    setSelectedBuilding(state, action: PayloadAction<Building | null>) {
      state.selectedBuilding = action.payload;
    },
    setSearchResults(state, action: PayloadAction<Building[]>) {
      state.searchResults = action.payload;
    },
    addBuilding(state, action: PayloadAction<Building>) {
      state.buildings.push(action.payload);
    },
    clearSearchResults(state) {
      state.searchResults = [];
    },
    setSearchPredictions(state, action: PayloadAction<Prediction[]>) {
      state.searchPredictions = action.payload;
    },
    clearSearchPredictions(state) {
      state.searchPredictions = [];
    },
    setSessionToken(state, action: PayloadAction<string | null>) {
      state.sessionToken = action.payload;
    },
    clearSessionToken(state) {
      state.sessionToken = null;
    },
    setFollowedBuildings(state, action: PayloadAction<Building[]>) {
      // Dedup by address_slug (or id) — NOT plus_code. Multiple distinct
      // addresses can share a plus_code now (parent address + its
      // subpremises), and deduping by plus_code would silently drop one of
      // them from the followed list.
      const seen = new Set<string>();
      state.followedBuildings = action.payload.filter(b => {
        const key = b.addressSlug || b.id;
        return seen.has(key) ? false : !!seen.add(key);
      });
    },
    followBuilding(state, action: PayloadAction<Building>) {
      const building = action.payload;
      const existingIdx = state.followedBuildings.findIndex(b => b.id === building.id);
      if (existingIdx === -1) {
        state.followedBuildings.push({ ...building, isFollowing: true });
      } else {
        // The building may already be in the list as an inferred parent of a
        // followed sub-address (isFollowing=false). Upgrade it to a real
        // follow rather than skipping the update — otherwise the Follow
        // button stays stuck on "Follow" after a successful follow.
        state.followedBuildings[existingIdx] = {
          ...state.followedBuildings[existingIdx],
          ...building,
          isFollowing: true,
        };
      }
      // Update isFollowing in other lists
      const updateIsFollowing = (buildings: Building[]) => {
        const index = buildings.findIndex(b => b.id === building.id);
        if (index !== -1) {
          buildings[index] = { ...buildings[index], isFollowing: true };
        }
      };
      updateIsFollowing(state.buildings);
      updateIsFollowing(state.nearbyBuildings);
      updateIsFollowing(state.searchResults);
      if (state.selectedBuilding?.id === building.id) {
        state.selectedBuilding = { ...state.selectedBuilding, isFollowing: true };
      }
    },
    unfollowBuilding(state, action: PayloadAction<string>) {
      const buildingId = action.payload;
      state.followedBuildings = state.followedBuildings.filter(b => b.id !== buildingId);
      // Update isFollowing in other lists
      const updateIsFollowing = (buildings: Building[]) => {
        const index = buildings.findIndex(b => b.id === buildingId);
        if (index !== -1) {
          buildings[index] = { ...buildings[index], isFollowing: false };
        }
      };
      updateIsFollowing(state.buildings);
      updateIsFollowing(state.nearbyBuildings);
      updateIsFollowing(state.searchResults);
      if (state.selectedBuilding?.id === buildingId) {
        state.selectedBuilding = { ...state.selectedBuilding, isFollowing: false };
      }
    },
  },
});

export const {
  loadBuildings,
  setNearbyBuildings,
  setUserLocation,
  setUserPlusCode,
  setSelectedBuilding,
  setSearchResults,
  addBuilding,
  clearSearchResults,
  setSearchPredictions,
  clearSearchPredictions,
  setSessionToken,
  clearSessionToken,
  setFollowedBuildings,
  followBuilding,
  unfollowBuilding,
} = buildingsSlice.actions;

export default buildingsSlice.reducer;
